/**
 * CookieReengageTip — Iter 67an.
 *
 * One-time tooltip anchored to the footer "Cookies" button. Surfaces only
 * when:
 *
 *   - The visitor's last consent decision was `essentials`, AND
 *   - The exit-intent watcher armed by `CookieConsentBanner.decide()` flagged
 *     a quick (< 30s) exit on a previous visit.
 *
 * After the tooltip is shown once we mark it `consumed=true` so the user
 * never sees it again, regardless of subsequent quick exits — this is a
 * single nudge, not a nag. Clicking the tooltip's "Enable analytics" CTA
 * fires the same event the footer Cookies link uses, re-opening the banner.
 */
import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { isEmbedSurface } from "@/lib/isEmbedSurface";

const REENGAGE_KEY = "rentalcar:cookie-reengage";
const CONSENT_KEY = "rentalcar:cookie-consent";

function _readJSON(key) {
  try { return JSON.parse(window.localStorage.getItem(key) || "null"); }
  catch { return null; }
}

export default function CookieReengageTip() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Iter 69 — embed surfaces must stay clean of overlay nudges.
    if (isEmbedSurface()) return;
    const reengage = _readJSON(REENGAGE_KEY);
    const consent = _readJSON(CONSENT_KEY);
    // Don't show if it never armed, was already consumed, or the user has
    // since accepted all cookies (in which case the tip is moot).
    if (!reengage?.pending || reengage?.consumed) return;
    if (!consent || consent.decision !== "essentials") return;

    // Show after a short delay so the page can settle / the user can find
    // the footer naturally. 1.8s feels deliberate without being slow.
    const tid = window.setTimeout(() => setShow(true), 1800);
    return () => window.clearTimeout(tid);
  }, []);

  const dismiss = (alsoOpenBanner = false) => {
    try {
      window.localStorage.setItem(REENGAGE_KEY, JSON.stringify({
        pending: false, consumed: true, consumed_at: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
    setShow(false);
    if (alsoOpenBanner) {
      window.dispatchEvent(new CustomEvent("rentalcar:open-cookie-banner"));
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-20 right-4 sm:right-6 max-w-xs z-[55] animate-in fade-in slide-in-from-bottom-2 duration-500"
      data-testid="cookie-reengage-tip"
      role="dialog"
      aria-live="polite"
    >
      <div className="rounded-2xl bg-gradient-to-br from-neutral-950 to-neutral-800 text-white shadow-2xl shadow-neutral-900/30 ring-1 ring-white/10 p-4 pr-9 relative">
        <button
          type="button"
          onClick={() => dismiss(false)}
          className="absolute top-2 right-2 h-6 w-6 rounded-full text-white/40 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
          title="Dismiss"
          data-testid="cookie-reengage-tip-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-start gap-2.5">
          <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
          <div className="space-y-2.5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300 mb-0.5">Tip</div>
              <div className="text-xs leading-relaxed text-white/85">
                Enable analytics cookies and we'll show you <strong className="text-white">better deals</strong> next time.
              </div>
            </div>
            <button
              type="button"
              onClick={() => dismiss(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200 transition"
              data-testid="cookie-reengage-tip-cta"
            >
              Enable analytics →
            </button>
          </div>
        </div>
        {/* Tail pointing to the footer Cookies link below. Doesn't have to be
            pixel-perfect since the footer position varies, but the small
            chevron makes it feel anchored rather than floating. */}
        <div className="absolute -bottom-1.5 right-10 h-3 w-3 rotate-45 bg-neutral-800 ring-1 ring-white/10" />
      </div>
    </div>
  );
}
